/**
 * WorkspacePage Component
 *
 * This is the main workspace overview page that displays:
 * - Workspace summary cards (Ask, Documents, Prompt Library, Activities)
 * - Recent ask sessions/conversations
 * - Recent documents
 * - Activity counts and statistics
 *
 * The component fetches workspace data, ask sessions, and activity counts
 * to provide a comprehensive overview of workspace activity and content.
 */

// ============= Imports =============

// React hooks
import React, { useCallback, useEffect, useState } from 'react';
import { useCompactTheme } from '@/hooks/useCompactTheme';
import styled from 'styled-components';

// Routing
import { useNavigate, useParams } from 'react-router-dom';

// Styled components
import {
  PageContainer,
  ContentSection,
  ContentSectionLists,
  PlaceholderText,
  ContentSectionContainer,
  CardGroup,
  Icon,
  Description,
  Count,
  Update,
  CompactCardContent,
  CompactCardDescription,
  CompactCardIcon,
  CompactCardMeta,
  CompactSummaryCard,
  CollapsibleSectionsStack,
  WorkspaceOverviewHero,
  WorkspaceOverviewSubtitle,
  WorkspaceOverviewTitle,
} from './WorkSpacePage.styles';

// Component library
import {
  Card,
  Headers,
  LoadingSpinner,
  Spacer,
  WorkspaceItem,
  WorkspaceList,
  formatDate,
} from '@tw/tw-component-library';
import {
  FiActivity,
  FiFileText,
  FiMessageCircle,
  FiPenTool,
} from 'react-icons/fi';

import {
  WorkspaceCollapsibleSection,
  useWorkspacePageSections,
} from './WorkspaceCollapsibleSection';

// Layout components
import { WorkspaceHeaderLayout } from '@/components/layout/WorkspaceHeaderLayout';

// APIs
import { getWorkspace } from '@/apis/core/workspace/get/getWorkspace';
import { getAskSessionsByWorkspace } from '@/apis/ask/askHistory/get/getAskSessionsByWorkspace';

// Contexts
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Hooks
import { useAuditLogActivities } from '@/components/common/Activities/ActivitiesPage';

// Utilities
import {
  transformAllUserWorkspaces,
  TransformedUserWorkspace,
} from '@/utils/data/transformWorkSpaceItems';

// Types
import { DataRoomFile } from '@/types/documents/documents.types';
import { AskSessionSession } from '@/apis/ask/askHistory/types';

// ============= Types =============

/**
 * Navigation route paths for workspace sections.
 */
type WorkspaceRoute =
  | 'ask-hub'
  | 'documents'
  | 'prompt-library'
  | 'activities'
  | `ask-session/${string}`;

// ============= Constants =============

/**
 * Placeholder text for missing data.
 */
const PLACEHOLDER_NA = 'N/A';

const CompactWorkspaceList = styled(WorkspaceList)`
  border: 1px solid ${({ theme }) => theme.colors.border.light} !important;
  border-radius: ${({ theme }) => theme.borderRadius.sm} !important;
  padding: 1rem !important;

  > div:first-child,
  > button:first-child {
    margin: -1rem -1rem 0.75rem -1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
    padding: 0.7rem 1rem;
  }

  h2 {
    font-size: 1rem;
    font-weight: 600;
    text-align: left;
  }
`;

// ============= Utilities =============

/**
 * Filters and validates DataRoomFile items from unknown array.
 * Removes deleted files and ensures proper type structure.
 *
 * @param items - Array of unknown items to filter
 * @returns Array of valid DataRoomFile items
 */
const filterValidDocuments = (items: unknown[]): DataRoomFile[] => {
  return items.filter((item): item is DataRoomFile => {
    const file = item as DataRoomFile;
    return (
      file &&
      typeof file === 'object' &&
      'dataRoomFileId' in file &&
      !file.markedForDeletion
    );
  });
};

/**
 * Sorts documents by added date (most recent first).
 *
 * @param documents - Array of documents to sort
 * @returns Sorted array of documents
 */
const sortDocumentsByDate = (documents: DataRoomFile[]): DataRoomFile[] => {
  return [...documents].sort((a, b) => {
    const dateA = new Date(a.added).getTime();
    const dateB = new Date(b.added).getTime();
    return dateB - dateA; // Descending order (most recent first)
  });
};

/**
 * Sorts ask sessions by created date (most recent first).
 *
 * @param sessions - Array of ask sessions to sort
 * @returns Sorted array of sessions
 */
const sortSessionsByDate = (
  sessions: AskSessionSession[],
): AskSessionSession[] => {
  return [...sessions].sort((a, b) => {
    const dateA = new Date(a.createdOn).getTime();
    const dateB = new Date(b.createdOn).getTime();
    return dateB - dateA; // Descending order (most recent first)
  });
};

/**
 * Formats the last update date for display.
 *
 * @param date - Date string or undefined
 * @returns Formatted date string or placeholder
 */
const formatLastUpdate = (date?: string): string => {
  return date ? formatDate(date) : PLACEHOLDER_NA;
};

/**
 * Formats the last document added date for display.
 *
 * @param documents - Array of documents
 * @returns Formatted date string or placeholder
 */
const formatLastDocumentDate = (documents: DataRoomFile[]): string => {
  return documents.length > 0 ? formatDate(documents[0].added) : PLACEHOLDER_NA;
};

// ============= Component =============

/**
 * WorkspacePage Component
 *
 * Main workspace overview page displaying summary cards and recent items.
 * Provides navigation to different workspace sections.
 *
 * @returns JSX.Element - The rendered workspace page
 */
export const WorkspacePage: React.FC = () => {
  const { isCompactTheme } = useCompactTheme();
  // ============= Hooks =============

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAccessToken } = UseAuth();
  const { setCurrentWorkspaceId } = useWorkspace();

  // ============= State =============

  /** Transformed workspace data with enhanced information */
  const [workspaceData, setWorkspaceData] =
    useState<TransformedUserWorkspace | null>(null);

  /** Most recent documents sorted by date */
  const [mostRecentDocuments, setMostRecentDocuments] = useState<
    DataRoomFile[]
  >([]);

  /** Loading state */
  const [isLoading, setIsLoading] = useState(true);

  /** Error state */
  const [error, setError] = useState<Error | null>(null);

  /** Ask sessions for the workspace */
  const [askSessions, setAskSessions] = useState<AskSessionSession[]>([]);

  /** Total activities count for today (only fetches once workspaceData is available) */
  const { totalActivitiesToday } = useAuditLogActivities(
    workspaceData
      ? {
          fetchActivities: false,
          fetchTodayCount: true,
          workspaceUserData: workspaceData.workspaceUsers?.userData,
          workspaceId: id || '',
        }
      : undefined,
  );

  const { sections, setSectionExpanded } = useWorkspacePageSections(id);

  // ============= Data Fetching =============

  /**
   * Fetches and transforms workspace data from the API.
   * Filters and sorts documents by most recent.
   */
  const getWorkspaceData = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch workspace data
      const data = await getWorkspace({
        getAccessToken: getAccessToken,
        workspaceId: id,
      });

      // Transform workspace data
      const transformed = await transformAllUserWorkspaces(
        [data],
        getAccessToken,
      );

      // Filter and sort documents
      const validDocuments = filterValidDocuments(
        transformed[0].dataRoom.workspaceDataRoomFiles.items,
      );
      const sortedDocuments = sortDocumentsByDate(validDocuments);

      // Update state
      setWorkspaceData({ ...transformed[0] });
      setMostRecentDocuments(sortedDocuments);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load workspace data');
      setError(error);
      console.error('Failed to load workspace data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id, getAccessToken]);

  /**
   * Fetches ask sessions for the workspace.
   * Sorts sessions by most recent first.
   */
  const fetchAskSessions = useCallback(async () => {
    if (!id) return;

    try {
      const sessions = await getAskSessionsByWorkspace({
        getAccessToken,
        workspaceId: id,
      });

      // Sort sessions by most recent first
      const sortedSessions = sortSessionsByDate(sessions);
      setAskSessions(sortedSessions);
    } catch (err) {
      console.error('Failed to load ask sessions:', err);
      // Don't set error state - this is a non-critical feature
    }
  }, [id, getAccessToken]);

  // ============= Effects =============

  /**
   * Fetch workspace data and ask sessions on mount and when ID changes.
   */
  useEffect(() => {
    getWorkspaceData();
    fetchAskSessions();
  }, [getWorkspaceData, fetchAskSessions]);

  /**
   * Update current workspace ID in context when route ID changes.
   */
  useEffect(() => {
    setCurrentWorkspaceId(id || null);
  }, [id, setCurrentWorkspaceId]);

  // ============= Event Handlers =============

  /**
   * Handles navigation to a workspace section.
   *
   * @param route - The route path to navigate to
   */
  const handleNavigateToSection = useCallback(
    (route: WorkspaceRoute) => {
      if (!workspaceData) return;
      navigate(`/workspace/${workspaceData.id}/${route}`);
    },
    [workspaceData, navigate],
  );

  /**
   * Handles navigation to an ask session.
   *
   * @param sessionId - The session ID to navigate to
   */
  const handleNavigateToSession = useCallback(
    (sessionId: string) => {
      if (!workspaceData) return;
      navigate(`/workspace/${workspaceData.id}/ask-session/${sessionId}`);
    },
    [workspaceData, navigate],
  );

  /**
   * Handles document item click by routing to the documents section.
   */
  const handleDocumentClick = useCallback(() => {
    handleNavigateToSection('documents');
  }, [handleNavigateToSection]);

  // ============= Render Helpers =============

  /**
   * Renders the Ask card with conversation count and last update.
   */
  const renderAskCard = () => {
    if (isCompactTheme) {
      return (
        <CompactSummaryCard
          hover
          onClick={() => handleNavigateToSection('ask-hub')}
          data-tour="workspace-page-ask-card"
        >
          <CompactCardIcon>
            <FiMessageCircle />
          </CompactCardIcon>
          <CompactCardContent>
            <Headers headerType="h3">Ask</Headers>
            <CompactCardDescription>
              Open Ask Hub and continue AI conversations.
            </CompactCardDescription>
            <CompactCardMeta>
              <strong>{askSessions.length} conversations</strong>
              <span>Last: {formatLastUpdate(workspaceData?.lastUpdate)}</span>
            </CompactCardMeta>
          </CompactCardContent>
        </CompactSummaryCard>
      );
    }

    return (
      <Card
        hover
        onClick={() => handleNavigateToSection('ask-hub')}
        data-tour="workspace-page-ask-card"
      >
        <Icon $isCompact={false}>💬</Icon>
        <Headers headerType="h3">Ask</Headers>
        <Spacer />
        <Description $isCompact={false}>Ask a question</Description>
        <Spacer />
        <Count $isCompact={false}>{askSessions.length} conversations</Count>
        <Update $isCompact={false}>
          Last: {formatLastUpdate(workspaceData?.lastUpdate)}
        </Update>
      </Card>
    );
  };

  /**
   * Renders the Documents card with document count and last update.
   */
  const renderDocumentsCard = () => {
    const documentCount =
      workspaceData?.dataRoom.workspaceDataRoomFiles.count || 0;

    if (isCompactTheme) {
      return (
        <CompactSummaryCard hover onClick={() => handleNavigateToSection('documents')}>
          <CompactCardIcon>
            <FiFileText />
          </CompactCardIcon>
          <CompactCardContent>
            <Headers headerType="h3">Documents</Headers>
            <CompactCardDescription>
              Review, search, and manage workspace documents.
            </CompactCardDescription>
            <CompactCardMeta>
              <strong>{documentCount} documents</strong>
              <span>Last: {formatLastDocumentDate(mostRecentDocuments)}</span>
            </CompactCardMeta>
          </CompactCardContent>
        </CompactSummaryCard>
      );
    }

    return (
      <Card hover onClick={() => handleNavigateToSection('documents')}>
        <Icon $isCompact={false}>📄</Icon>
        <Headers headerType="h3">Documents</Headers>
        <Spacer />
        <Description $isCompact={false}>
          View and manage documents in the workspace
        </Description>
        <Spacer />
        <Count $isCompact={false}>{documentCount} documents</Count>
        <Update $isCompact={false}>
          Last: {formatLastDocumentDate(mostRecentDocuments)}
        </Update>
      </Card>
    );
  };

  /**
   * Renders the Prompt Library card.
   */
  const renderPromptLibraryCard = () => {
    if (isCompactTheme) {
      return (
        <CompactSummaryCard
          hover
          onClick={() => handleNavigateToSection('prompt-library')}
        >
          <CompactCardIcon>
            <FiPenTool />
          </CompactCardIcon>
          <CompactCardContent>
            <Headers headerType="h3">Prompt Library</Headers>
            <CompactCardDescription>
              Access reusable prompts and persona-driven templates.
            </CompactCardDescription>
          </CompactCardContent>
        </CompactSummaryCard>
      );
    }

    return (
      <Card hover onClick={() => handleNavigateToSection('prompt-library')}>
        <Icon $isCompact={false}>🎭</Icon>
        <Headers headerType="h3">Prompt Library</Headers>
        <Spacer />
        <Description $isCompact={false}>
          View and manage prompts in the workspace
        </Description>
        <Spacer />
      </Card>
    );
  };

  /**
   * Renders the Activities card with today's activity count.
   */
  const renderActivitiesCard = () => {
    if (isCompactTheme) {
      return (
        <CompactSummaryCard hover onClick={() => handleNavigateToSection('activities')}>
          <CompactCardIcon>
            <FiActivity />
          </CompactCardIcon>
          <CompactCardContent>
            <Headers headerType="h3">Activities</Headers>
            <CompactCardDescription>
              Track collaboration and document activity signals.
            </CompactCardDescription>
            <CompactCardMeta>
              <strong>{totalActivitiesToday} today</strong>
            </CompactCardMeta>
          </CompactCardContent>
        </CompactSummaryCard>
      );
    }

    return (
      <Card hover onClick={() => handleNavigateToSection('activities')}>
        <Icon $isCompact={false}>📈</Icon>
        <Headers headerType="h3">Activities</Headers>
        <Spacer />
        <Description $isCompact={false}>
          View and manage activities in the workspace
        </Description>
        <Spacer />
        <Count $isCompact={false}>{totalActivitiesToday} activities today</Count>
      </Card>
    );
  };

  // ============= Early Returns =============

  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  if (error) {
    return (
      <PageContainer>
        <ContentSectionContainer $isCompact={isCompactTheme}>
          <ContentSection $isCompact={isCompactTheme}>
            <PlaceholderText>
              Error loading workspace: {error.message}
            </PlaceholderText>
          </ContentSection>
        </ContentSectionContainer>
      </PageContainer>
    );
  }

  // ============= Render =============

  const WorkspaceListComponent = isCompactTheme
    ? CompactWorkspaceList
    : WorkspaceList;

  const summarySectionTitle = isCompactTheme
    ? 'Quick access'
    : 'Workspace areas';
  const conversationsSectionTitle = isCompactTheme
    ? 'Recent conversations'
    : 'Recent Asks';
  const documentsSectionTitle = isCompactTheme
    ? 'Recent files'
    : 'Recent Documents';

  return (
    <PageContainer>
      {!isLoading && !error && workspaceData && (
        <>
          <WorkspaceHeaderLayout
            workspaceData={workspaceData}
            activePage="workspace"
          />
          <ContentSectionContainer $isCompact={isCompactTheme}>
            <ContentSection $isCompact={isCompactTheme}>
              {isCompactTheme && (
                <WorkspaceOverviewHero>
                  {/* // TODO Not sure if i like these or not, not sure if it's just stating the obvious. */}
                  {/* <WorkspaceOverviewEyebrow>
                    <FiBriefcase />
                    Workspace overview
                  </WorkspaceOverviewEyebrow> */}
                  <WorkspaceOverviewTitle>
                    {workspaceData.title || 'Workspace dashboard'}
                  </WorkspaceOverviewTitle>
                  <WorkspaceOverviewSubtitle>
                    Jump into conversations, documents, prompts, and activity
                    from one focused workspace home.
                  </WorkspaceOverviewSubtitle>
                </WorkspaceOverviewHero>
              )}

              <CollapsibleSectionsStack $isCompact={isCompactTheme}>
                <WorkspaceCollapsibleSection
                  title={summarySectionTitle}
                  expanded={sections.summary}
                  onExpandedChange={(expanded) =>
                    setSectionExpanded('summary', expanded)
                  }
                  data-tour="workspace-page-summary-section"
                >
                  <CardGroup $isCompact={isCompactTheme}>
                    {renderAskCard()}
                    {renderDocumentsCard()}
                    {renderPromptLibraryCard()}
                    {renderActivitiesCard()}
                  </CardGroup>
                </WorkspaceCollapsibleSection>

                <ContentSectionLists $isCompact={isCompactTheme}>
                  <WorkspaceCollapsibleSection
                    title={conversationsSectionTitle}
                    hint={
                      isCompactTheme
                        ? 'Latest ask sessions in this workspace. Open one to continue where you left off.'
                        : undefined
                    }
                    expanded={sections.conversations}
                    onExpandedChange={(expanded) =>
                      setSectionExpanded('conversations', expanded)
                    }
                    data-tour="workspace-page-conversations-section"
                  >
                    <WorkspaceListComponent
                      {...{
                        title: isCompactTheme ? 'Conversations' : 'Recent Asks',
                        variant: isCompactTheme ? 'activity' : 'default',
                        items: askSessions.map((session) => ({
                          id: session.id,
                          title: session.askTitle || 'Untitled conversation',
                          lastUsed: formatDate(session.createdOn),
                          recentActivityAction: 'Updated',
                          recentActivityEntityType: 'AskSession',
                        })),
                        enableSearch: true,
                        onItemClick: (item: WorkspaceItem) =>
                          handleNavigateToSession(item.id),
                        searchPlaceholder: 'Search conversations...',
                        enablePagination: true,
                        itemsPerPage: 5,
                        showMemberPresence: false,
                      }}
                    />
                  </WorkspaceCollapsibleSection>

                  <WorkspaceCollapsibleSection
                    title={documentsSectionTitle}
                    hint={
                      isCompactTheme
                        ? 'Documents added or updated recently in this workspace.'
                        : undefined
                    }
                    expanded={sections.documents}
                    onExpandedChange={(expanded) =>
                      setSectionExpanded('documents', expanded)
                    }
                    data-tour="workspace-page-documents-section"
                  >
                    <WorkspaceListComponent
                      {...{
                        title: isCompactTheme ? 'Files' : 'Recent Documents',
                        variant: isCompactTheme ? 'activity' : 'default',
                        items: mostRecentDocuments.map((document) => ({
                          id: document.dataRoomFileId,
                          title: document.name,
                          lastUsed: formatDate(document.added),
                          recentActivityAction: 'Added',
                          recentActivityEntityType: 'DataRoomFile',
                        })),
                        enableSearch: true,
                        onItemClick: (item: WorkspaceItem) => {
                          if (isCompactTheme && workspaceData) {
                            navigate(
                              `/workspace/${workspaceData.id}/documents?fileId=${item.id}`,
                            );
                            return;
                          }
                          handleDocumentClick();
                        },
                        searchPlaceholder: 'Search documents...',
                        enablePagination: true,
                        itemsPerPage: 5,
                        showMemberPresence: false,
                      }}
                    />
                  </WorkspaceCollapsibleSection>
                </ContentSectionLists>
              </CollapsibleSectionsStack>
            </ContentSection>
          </ContentSectionContainer>
        </>
      )}
    </PageContainer>
  );
};
